/**
 * ShareGateControls — reusable panel to configure a share/embed gate:
 * password protection, expiry date, and (embed only) domain allowlist.
 *
 * Loads current settings from /dashboards/:id/share-config on mount, and calls
 * ``/{scope}/config`` (scope="share"|"embed") with only the fields the user changed.
 * Emits ``onChange(patch)`` after each save so the parent can update its optimistic UI.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KeyRound, CalendarClock, Globe, ShieldCheck, X, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export function ShareGateControls({ dashboardId, scope = "share", onChange }) {
  const [cfg, setCfg] = useState(null);      // {password_protected, expires_at, allowed_domains?}
  const [pw, setPw] = useState("");          // new password to set (empty = no change)
  const [expiry, setExpiry] = useState("");  // yyyy-mm-dd (empty = no change)
  const [domain, setDomain] = useState("");
  const [domains, setDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const showDomains = scope === "embed";

  const load = async () => {
    try {
      const { data } = await api.get(`/dashboards/${dashboardId}/share-config`);
      const c = data[scope] || {};
      setCfg(c);
      setDomains(c.allowed_domains || []);
      setExpiry(c.expires_at ? c.expires_at.slice(0, 10) : "");
    } catch { /* ignore */ }
  };
  useEffect(() => { if (dashboardId) load(); }, [dashboardId, scope]);

  const currentExpiry = cfg?.expires_at ? new Date(cfg.expires_at) : null;
  const expiryLabel = currentExpiry
    ? currentExpiry.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
    : "Never expires";

  const patch = useMemo(() => {
    const p = {};
    if (pw !== "") p.password = pw;
    if (expiry !== "" && (cfg?.expires_at || "").slice(0, 10) !== expiry) {
      p.expires_at = `${expiry}T23:59:59Z`;
    }
    if (showDomains) {
      const cur = cfg?.allowed_domains || [];
      if (domains.length !== cur.length || domains.some((d, i) => d !== cur[i])) {
        p.allowed_domains = domains;
      }
    }
    return p;
  }, [pw, expiry, cfg, domains, showDomains]);

  const save = async () => {
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    try {
      await api.post(`/dashboards/${dashboardId}/${scope}/config`, patch);
      toast.success("Settings saved");
      setPw("");
      await load();
      onChange && onChange(patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const removePassword = async () => {
    setSaving(true);
    try {
      await api.post(`/dashboards/${dashboardId}/${scope}/config`, { password: "" });
      toast.success("Password removed");
      setPw("");
      await load();
      onChange && onChange({ password: "" });
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  const removeExpiry = async () => {
    setSaving(true);
    try {
      await api.post(`/dashboards/${dashboardId}/${scope}/config`, { expires_at: "" });
      toast.success("Expiry removed");
      setExpiry("");
      await load();
      onChange && onChange({ expires_at: "" });
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const addDomain = () => {
    const d = domain.trim().toLowerCase();
    if (!d) return;
    if (domains.includes(d)) { toast.error("Already added"); return; }
    setDomains((cur) => [...cur, d]);
    setDomain("");
  };
  const removeDomain = (d) => setDomains((cur) => cur.filter((x) => x !== d));

  return (
    <div data-testid={`gate-${scope}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-3">
      <div className="flex items-center gap-2 text-xs font-medium text-[color:var(--ink-2)]">
        <ShieldCheck className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        Access controls
      </div>

      {/* Password */}
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1"><KeyRound className="w-3 h-3" />Password</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input
            data-testid={`gate-${scope}-password`}
            type="password"
            placeholder={cfg?.password_protected ? "•••••••• (change)" : "Set a password (optional)"}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="bg-[color:var(--surface)] text-xs"
          />
          {cfg?.password_protected && (
            <Button data-testid={`gate-${scope}-password-clear`} size="sm" variant="ghost" onClick={removePassword} className="text-[color:var(--danger)]" title="Remove password">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        {cfg?.password_protected && (
          <div className="text-[10px] text-[color:var(--brand-ocean)] mt-1">Currently password-protected</div>
        )}
      </div>

      {/* Expiry */}
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1"><CalendarClock className="w-3 h-3" />Expires</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <Input
            data-testid={`gate-${scope}-expiry`}
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            className="bg-[color:var(--surface)] text-xs"
          />
          {cfg?.expires_at && (
            <Button data-testid={`gate-${scope}-expiry-clear`} size="sm" variant="ghost" onClick={removeExpiry} className="text-[color:var(--danger)]" title="Remove expiry">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <div className="text-[10px] text-[color:var(--ink-3)] mt-1">Currently: <b>{expiryLabel}</b></div>
      </div>

      {/* Domain allowlist (embed only) */}
      {showDomains && (
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)] flex items-center gap-1"><Globe className="w-3 h-3" />Allowed domains (embed)</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              data-testid={`gate-${scope}-domain-input`}
              placeholder="e.g. app.example.com or *.example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDomain(); } }}
              className="bg-[color:var(--surface)] text-xs"
            />
            <Button data-testid={`gate-${scope}-domain-add`} size="sm" onClick={addDomain}>Add</Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {domains.length === 0 && (
              <span className="text-[10px] text-[color:var(--ink-3)] italic">Empty allowlist = any origin can embed.</span>
            )}
            {domains.map((d) => (
              <Badge key={d} variant="outline" className="text-[10px] py-0 h-5 gap-1">
                {d}
                <button data-testid={`gate-${scope}-domain-remove-${d}`} onClick={() => removeDomain(d)} className="hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          data-testid={`gate-${scope}-save`}
          size="sm"
          onClick={save}
          disabled={saving || Object.keys(patch).length === 0}
          className="bg-[color:var(--brand-ocean)] text-white"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Save access controls
        </Button>
      </div>
    </div>
  );
}
